"use client";

import React, { useState, useRef, useEffect } from "react";
import Link from "next/link";
import Image from "next/image";
import { sanitizeRentalContent } from "@/utils/sanitizeRentalContent";

const MonthlyCities = ({ seoAllData }) => {
  const [showFullContent, setShowFullContent] = useState(false);
  const [longCitiesLists, setLongCitiesLists] = useState(null);
  const contentRef = useRef(null);
  const scrollRef = useRef(null);

  const toggleContent = () => setShowFullContent((prev) => !prev);

  const isScrollable = seoAllData?.shortContent?.length > 500;
  const rawContent = showFullContent
    ? seoAllData?.shortContent
    : seoAllData?.shortContent?.slice(0, 500);
  const contentToDisplay = sanitizeRentalContent(rawContent, {
    h1: seoAllData?.h1,
  });

  useEffect(() => {
    const fetchCities = async () => {
      try {
        const response = await fetch(
          `${process.env.NEXT_PUBLIC_API_BASE_URL}/data/cities?longRentStatus=1`
        );
        const data = await response.json();
        if (response.ok) {
          setLongCitiesLists(data);
        }
      } catch {
      }
    };

    fetchCities();
  }, []);

  useEffect(() => {
    if (showFullContent && contentRef.current) {
      contentRef.current.scrollTop = 0;
    }
  }, [showFullContent]);

  if (!longCitiesLists?.data?.length || !seoAllData) return null;

  return (
    <section className="bg-transparent text-center flex flex-col items-center" aria-labelledby="monthly-cities-heading">
      <div className="mt-10 max-lg:mt-8 scroll-mt-24 flex flex-col items-center gap-3 w-full px-[60px] max-lg:px-4">
        <h2
          id="monthly-cities-heading"
          className="font-poppins text-[30px] lg:text-[38px] max-lg:text-2xl font-semibold leading-tight text-center text-textPrimary max-w-2xl"
        >
          New to the city, want to travel like a local?
        </h2>
        <p className="text-lg lg:text-xl font-medium text-textSecondary text-center">
          Explore our long-term rental options
        </p>

        {/* City Cards - Horizontal Scroll */}
        <div
          ref={scrollRef}
          className="w-full overflow-x-auto pt-2 pb-4 [scrollbar-width:thin] [scrollbar-color:rgba(0,0,0,0.2)_transparent] [&::-webkit-scrollbar]:h-1.5 [&::-webkit-scrollbar-thumb]:bg-black/20 [&::-webkit-scrollbar-thumb]:rounded-[32px]"
        >
          {/* City images are 70px circular avatar PNGs (transparent corners) — render
              them at native size as crisp round avatars rather than stretching them
              full-bleed (which upscales ~3x and shows faded corners). */}
          <div className="flex gap-4 lg:gap-6 w-max px-1">
            {longCitiesLists.data.map((item, index) => (
              <Link
                href={`/monthly-bike-rental-in-${item?.name?.toLowerCase().replace(/\s+/g, "-")}/`}
                key={item.id || index}
                className="group shrink-0 flex flex-col items-center gap-3 w-[124px] max-lg:w-[104px] p-4 max-lg:p-3 rounded-card-lg bg-bgCard shadow-card transition-[transform,box-shadow] duration-200 ease-in-out hover:-translate-y-1 hover:shadow-card-lift"
              >
                {item.imageUrl ? (
                  <Image
                    src={item.imageUrl}
                    alt={item.name || "Unknown City"}
                    width={80}
                    height={80}
                    className="w-20 h-20 max-lg:w-[72px] max-lg:h-[72px] rounded-full object-cover ring-1 ring-line transition-transform duration-300 group-hover:scale-105"
                  />
                ) : (
                  // no image from API — initial-letter avatar (avoids showing the wrong city's photo)
                  <span className="w-20 h-20 max-lg:w-[72px] max-lg:h-[72px] grid place-items-center rounded-full bg-redSurface-50 ring-1 ring-line text-2xl font-bold text-primary transition-transform duration-300 group-hover:scale-105">
                    {(item.name || "?").charAt(0).toUpperCase()}
                  </span>
                )}
                <span className="text-sm font-semibold text-textPrimary text-center leading-tight transition-colors group-hover:text-primary">
                  {item.name || "Unnamed City"}
                </span>
              </Link>
            ))}
          </div>
        </div>

        {/* SEO Content Section. Heading is H2 — the page H1 is rendered by the
            parent server component (MonthlyRental). This block is client-fetched
            and would otherwise leave Google's HTML crawl with no H1 on this page. */}
        <div className="w-full max-w-[820px] mt-12 mb-4">
          <h2 className="font-poppins text-2xl lg:text-[28px] max-lg:text-xl font-semibold text-textPrimary text-center mb-5">
            {seoAllData?.h1}
          </h2>
          <div className="dynamic-code-content">
            <div
              ref={contentRef}
              className={`mx-auto ${showFullContent ? "max-h-[280px] overflow-y-auto [scrollbar-width:thin] [scrollbar-color:rgba(0,0,0,0.2)_transparent] [&::-webkit-scrollbar]:w-[5px] [&::-webkit-scrollbar-thumb]:bg-black/20 [&::-webkit-scrollbar-thumb]:rounded-[5px]" : "max-h-[72px] overflow-hidden [mask-image:linear-gradient(to_bottom,black_60%,transparent_100%)] [-webkit-mask-image:linear-gradient(to_bottom,black_60%,transparent_100%)]"}`}
              id="short-content"
            >
              <div
                className="text-base lg:text-[17px] font-normal leading-relaxed text-textSecondary text-center"
                dangerouslySetInnerHTML={{ __html: contentToDisplay }}
              />
            </div>
          </div>

          {isScrollable && (
            <button
              className="show-link-search mt-4 inline-flex items-center gap-1 font-semibold text-sm text-primary hover:text-primary-dark bg-transparent border-none cursor-pointer min-h-[44px] px-2 py-2"
              onClick={toggleContent}
              aria-expanded={showFullContent}
              aria-controls="short-content"
            >
              {showFullContent ? "Show less" : "Show more"}
            </button>
          )}
        </div>
      </div>
    </section>
  );
};

export default MonthlyCities;
